import { useCallback, useEffect, useState } from 'react';
import { lookupAssetByTag, normalizeLookupResult, submitAsset } from '../api';
import { Confetti } from '../components/Confetti';
import { FormField } from '../components/FormField';
import { ReviewModal } from '../components/ReviewModal';
import { Toasts } from '../components/Toasts';
import {
  CONDITIONS,
  COUNTRIES,
  EMPTY_FORM,
  EQUIPMENT_TYPES,
  LOOKUP_SOURCE_ASSET_TIGER,
  LOOKUP_SOURCE_REGISTER,
  REQUIRED_FIELDS,
} from '../constants';
import { countFilledFields, progressFillClass, toISODate } from '../utils';

function tagFromSearch(search) {
  return new URLSearchParams(search).get('tag') || '';
}

function formFromLookup(result, searchedTag) {
  const fromAssetTiger = result.source === LOOKUP_SOURCE_ASSET_TIGER;

  return {
    ...EMPTY_FORM,
    asset_tag: result.asset_tag || searchedTag,
    brand_model: result.brand_model || '',
    serial_number: result.serial_number || '',
    project_number: fromAssetTiger ? '' : (result.project_number || ''),
    equipment_type: result.equipment_type || '',
    condition: result.condition || '',
    country: fromAssetTiger ? '' : (result.country || ''),
    assignee_email: fromAssetTiger ? '' : (result.assignee_email || ''),
    status: fromAssetTiger ? 'Active' : (result.status || 'Active'),
  };
}

export function CheckInOutPage({ user, search, isOnline }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [assetExists, setAssetExists] = useState(false);
  const [assetSource, setAssetSource] = useState(null);

  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionType, setActionType] = useState(null);

  const [showReview, setShowReview] = useState(false);
  const [reviewErrors, setReviewErrors] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'info') => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((current) => [...current, { id, message, type }]);
    setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, 5000);
  }, []);

  const lookupAsset = useCallback(async (assetTag) => {
    if (!assetTag.trim()) {
      setAssetExists(false);
      setAssetSource(null);
      return;
    }

    setIsLookingUp(true);

    try {
      const result = normalizeLookupResult(await lookupAssetByTag(assetTag));

      if (result.exists) {
        const fromAssetTiger = result.source === LOOKUP_SOURCE_ASSET_TIGER;
        setFormData(formFromLookup(result, assetTag));
        setAssetExists(true);
        setAssetSource(fromAssetTiger ? LOOKUP_SOURCE_ASSET_TIGER : LOOKUP_SOURCE_REGISTER);
        showToast(
          fromAssetTiger
            ? 'Not in the register — loaded from Asset Tiger.'
            : 'Asset found! Data loaded.',
          'success'
        );
      } else {
        setFormData((prev) => ({ ...EMPTY_FORM, asset_tag: assetTag }));
        setAssetExists(false);
        setAssetSource(null);
        showToast('New asset - enter details below.', 'info');
      }
    } catch (error) {
      console.error('Lookup error:', error);
      setAssetSource(null);
      showToast('Lookup failed - please try again', 'error');
    } finally {
      setIsLookingUp(false);
    }
  }, [showToast]);

  useEffect(() => {
    const tag = tagFromSearch(search);
    if (!tag.trim()) return;
    setFormData((prev) => ({ ...prev, asset_tag: tag }));
    lookupAsset(tag);
  }, [search, lookupAsset]);

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === 'Escape') {
        setShowReview(false);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    const nextValue = name === 'asset_tag' ? value.replace(/^[^0-9]+/, '') : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
  };

  const handleAssetTagBlur = () => {
    if (formData.asset_tag.trim()) {
      lookupAsset(formData.asset_tag);
    }
  };

  const validateForm = useCallback((action) => {
    const errors = [];
    if (!formData.asset_tag.trim()) errors.push('Asset Tag Number is required.');
    if (!formData.brand_model.trim()) errors.push('Brand and Model is required.');
    if (!formData.equipment_type) errors.push('Equipment Type is required.');
    if (!formData.condition) errors.push('Condition is required.');
    if (action === 'checkout' && !formData.assignee_email.trim()) {
      errors.push('Assignee Email is required for checkout.');
    }
    return errors;
  }, [formData]);

  const openReview = (action) => {
    if (isSubmitting) return;
    setActionType(action);
    setReviewErrors(validateForm(action));
    setShowReview(true);
  };

  const confirmAndSubmit = async () => {
    setShowReview(false);
    setIsSubmitting(true);

    const isCheckout = actionType === 'checkout';
    setSubmissionStatus({
      type: 'processing',
      message: `${isCheckout ? 'Checking out' : 'Checking in'} asset...`,
    });

    try {
      await submitAsset({
        ...formData,
        action: actionType,
        status: isCheckout ? 'Active' : 'Returned',
        date: toISODate(new Date()),
        submitted_by: user?.userDetails || 'Unknown User',
        submitted_by_email: user?.userDetails || user?.userId || 'unknown@pacgroup.com',
        submitted_at: new Date().toISOString(),
      });

      const successMsg = isCheckout
        ? 'Asset checked out successfully!'
        : 'Asset checked in successfully!';

      setSubmissionStatus({ type: 'success', message: successMsg });
      showToast(successMsg, 'success');
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
      setFormData(EMPTY_FORM);
      setAssetExists(false);
      setAssetSource(null);
    } catch (error) {
      setSubmissionStatus({
        type: 'error',
        message: `Submission error: ${error.message}`,
      });
      showToast('Submission failed', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldCount = countFilledFields(formData, REQUIRED_FIELDS);
  const progressPercent = (fieldCount.filled / fieldCount.total) * 100;

  const statusTitles = {
    success: '✅ Success!',
    error: '❌ Error',
    processing: '⏳ Working',
  };

  return (
    <>
      <Toasts toasts={toasts} />
      {showConfetti && <Confetti />}
      {showReview && (
        <ReviewModal
          actionType={actionType}
          formData={formData}
          errors={reviewErrors}
          isOnline={isOnline}
          onBack={() => setShowReview(false)}
          onConfirm={confirmAndSubmit}
        />
      )}

      <div className="panel">
        <div className="progress-header">
          <span className="progress-label">Form Completion</span>
          <span className="progress-percent">{Math.round(progressPercent)}%</span>
        </div>
        <div className="progress-track">
          <div
            className={`progress-fill ${progressFillClass(progressPercent)}`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className="progress-caption">
          {fieldCount.filled} of {fieldCount.total} required fields completed
        </div>
      </div>

      <div className="form-card">
        <div className="form-card-header">
          <div>
            <p className="eyebrow">Check in / out</p>
            <h2>Asset Information</h2>
          </div>
          {assetExists && (
            <span className={`found-badge${assetSource === LOOKUP_SOURCE_ASSET_TIGER ? ' is-tiger' : ''}`}>
              {assetSource === LOOKUP_SOURCE_ASSET_TIGER
                ? 'Loaded from Asset Tiger'
                : '✓ Asset Found in System'}
            </span>
          )}
        </div>
        <div className="form-grid">
          <FormField
            label="Asset Tag Number"
            name="asset_tag"
            value={formData.asset_tag}
            onChange={handleChange}
            onBlur={handleAssetTagBlur}
            required
            placeholder="Enter asset tag number"
            busy={isLookingUp}
          />

          <FormField
            label="Brand and Model"
            name="brand_model"
            value={formData.brand_model}
            onChange={handleChange}
            required
            placeholder="e.g., Dell Latitude 5520"
            disabled={isLookingUp}
          />
          <FormField
            label="Serial Number"
            name="serial_number"
            value={formData.serial_number}
            onChange={handleChange}
            placeholder="e.g. 5CG1234ABC"
            disabled={isLookingUp}
          />
          <FormField
            label="Project Number"
            name="project_number"
            value={formData.project_number}
            onChange={handleChange}
            placeholder="e.g. PRJ-12345"
            disabled={isLookingUp}
          />
          <FormField
            label="Equipment Type"
            name="equipment_type"
            value={formData.equipment_type}
            onChange={handleChange}
            required
            options={EQUIPMENT_TYPES}
            placeholder="Select equipment type"
            disabled={isLookingUp}
          />
          <FormField
            label="Condition"
            name="condition"
            value={formData.condition}
            onChange={handleChange}
            required
            options={CONDITIONS}
            placeholder="Select condition"
            disabled={isLookingUp}
          />
          <FormField
            label="Country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            options={COUNTRIES}
            placeholder="Select country"
            disabled={isLookingUp}
          />
          <FormField
            label="Assignee Email"
            name="assignee_email"
            value={formData.assignee_email}
            onChange={handleChange}
            type="email"
            placeholder="user@pacgroup.com"
            disabled={isLookingUp}
          />
          <FormField
            label="Status"
            name="status"
            value={formData.status}
            readOnly
            placeholder="Set automatically"
          />
        </div>

        <div className="action-panel">
          <h3>Choose an action:</h3>
          <div className="action-grid">
            <div className="action-card action-card-checkout">
              <h4>📤 Check Out Asset</h4>
              <p>Assign this asset to the specified user. Status will be set to "Active".</p>
              <button
                type="button"
                onClick={() => openReview('checkout')}
                disabled={isSubmitting || !isOnline}
                className="btn btn-checkout btn-block"
              >
                {isSubmitting && actionType === 'checkout' ? 'Processing...' : '📤 Check Out'}
              </button>
            </div>
            <div className="action-card action-card-checkin">
              <h4>📥 Check In Asset</h4>
              <p>Return this asset to the office. Assignee email is kept from the asset lookup.</p>
              <button
                type="button"
                onClick={() => openReview('checkin')}
                disabled={isSubmitting || !isOnline}
                className="btn btn-checkin btn-block"
              >
                {isSubmitting && actionType === 'checkin' ? 'Processing...' : '📥 Check In'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {submissionStatus && (
        <div className={`status-banner is-${submissionStatus.type}`}>
          <div className="status-title">{statusTitles[submissionStatus.type]}</div>
          <div className="status-message">{submissionStatus.message}</div>
        </div>
      )}
    </>
  );
}
